<template>
  <AdminAppLayout title="HR-DOS | Edit Requests">
    
    <div class="row" style="margin-bottom: 20px;">
      <div class="col s12">
        <h4 class="blue-text text-darken-2" style="font-weight: 600;">Pending Edit Requests</h4>
        <p class="grey-text">Review and approve changes made to employee/s</p>
      </div>
    </div>

    <div class="divider" style="margin-bottom: 25px; border-bottom: 2px dashed #283593;"></div>

    <!-- Loading Skeleton -->
    <div class="table-responsive-wrapper hide-on-med-and-down" v-show="loader">
      <table class="highlight">
        <thead>
          <tr>
            <th>Date Submitted</th>
            <th>LTO ID</th>
            <th>Employee Name</th>
            <th class="center-align">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="n in 3" :key="'skeleton-' + n">
            <td><div class="skeleton skeleton-text" style="width: 120px;"></div></td>
            <td><div class="skeleton skeleton-text" style="width: 100px;"></div></td>
            <td><div class="skeleton skeleton-text" style="width: 180px;"></div></td>
            <td class="center-align"><div class="skeleton skeleton-button" style="width: 200px; margin: 0 auto;"></div></td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row hide-on-large-only" v-show="loader">
      <div v-for="n in 3" :key="'skel-mob-' + n" class="col s12 m6">
        <div class="card" style="box-shadow: none; border: 1px solid #e0e0e0; border-radius: 8px;">
          <div class="card-content">
            <div class="skeleton skeleton-text" style="width: 70%; height: 18px; margin-bottom: 12px;"></div>
            <div class="skeleton skeleton-text" style="width: 50%; height: 14px; margin-bottom: 8px;"></div>
            <div class="skeleton skeleton-text" style="width: 60%; height: 14px;"></div>
          </div>
          <div class="card-action center-align">
            <div class="skeleton skeleton-button" style="display: inline-block; width: 80px; margin: 2px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Data Table -->
    <div class="table-responsive-wrapper hide-on-med-and-down" v-show="!loader">
      <table class="highlight bordered">
        <thead class="blue lighten-5">
          <tr>
            <th>Date Submitted</th>
            <th class="nowrap">LTO ID</th>
            <th>Employee Name</th>
            <th class="center-align nowrap">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!requests.length">
            <td colspan="4" class="center-align grey-text" style="padding: 40px 0;">
              <i class="material-icons" style="font-size: 3rem; opacity: 0.5;">fact_check</i>
              <p style="font-size: 1.2rem; margin-top: 10px;">No pending edit requests to review.</p>
            </td>
          </tr>

          <tr v-else v-for="request in requests" :key="request.id">
            <td class="nowrap"><b>{{ formatDate(request.created_at) }}</b></td>
            <td class="nowrap">{{ request.employee?.employee_id_number || 'N/A' }}</td>
            <td style="font-weight: 500; font-size: 1.1rem;">
              {{ request.employee?.first_name }} {{ request.employee?.last_name }}
            </td>
            <td class="center-align nowrap">
              <button 
                @click="openViewModal(request)" 
                class="btn-small yellow darken-4" 
                style="border-radius: 4px; margin-right: 8px;"
              >
                <b>View Details</b>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="row hide-on-large-only" v-show="!loader">
      <div class="col s12" v-if="!requests.length">
        <div class="center-align grey-text" style="padding: 40px 0;">
          <i class="material-icons" style="font-size: 3rem; opacity: 0.5;">fact_check</i>
          <p style="font-size: 1.2rem; margin-top: 10px;">No pending edit requests.</p>
        </div>
      </div>

      <div v-else class="col s12 m6" v-for="request in requests" :key="'mob-' + request.id">
        <div class="card" style="border-radius: 8px; border: 1px solid #e0e0e0; box-shadow: none;">
          <div class="card-content">
            <span class="card-title" style="font-size: 1.2rem; font-weight: bold; margin-bottom: 15px; color: #1565c0;">
              {{ request.employee?.first_name }} {{ request.employee?.last_name }}
            </span>
            <p style="margin-bottom: 5px;">
              <span class="grey-text">Date:</span> <b>{{ formatDate(request.created_at) }}</b>
            </p>
            <p>
              <span class="grey-text">LTO ID:</span> <b>{{ request.employee?.employee_id_number || 'N/A' }}</b>
            </p>
          </div>
          <div class="card-action center-align" style="background-color: #f9f9f9; border-radius: 0 0 8px 8px; padding: 12px 16px;">
            <button @click="openViewModal(request)" class="btn-small yellow darken-4" style="border-radius: 4px; margin: 3px; width: 100%;">
              <b>View Details</b>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div id="viewEditDiv" class="modal modal-fixed-footer main-modal">
      <div class="modal-content white lighten-4" style="padding: 0;">
        <div class="modal-header blue" style="display: flex; justify-content: space-between; align-items: center; padding: 15px 24px; position: sticky; top: 0; z-index: 10;">
          <h5 class="white-text" style="margin: 0; font-weight: 600;">Requested Changes</h5>
          <a href="#!" class="modal-close white-text close-x-btn" style="display: flex; align-items: center; justify-content: center; padding: 4px; border-radius: 50%;">
            <i class="material-icons" style="font-size: 2rem;">close</i>
          </a>
        </div>

        <div style="padding: 24px;">
          <p class="grey-text text-darken-2" style="margin-top: 0; font-size: 1.1rem;" v-if="selectedRequest">
            Submitted by: <b>{{ requestedBy }}</b>
          </p>
          <p class="grey-text text-darken-2 hide-on-small-only">Below is a comparison of the employee's current profile versus their requested edits. Changed fields are highlighted in green.</p>
          
          <table class="bordered highlight white hide-on-med-and-down" style="border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden;" v-if="selectedRequest && selectedRequest.requested_data">
            <thead class="grey lighten-4">
              <tr>
                <th style="width: 25%; padding-left: 15px;">Field</th>
                <th style="width: 35%;">Current Value</th>
                <th style="width: 40%;">Requested Change</th>
              </tr>
            </thead> 
            <tbody>
              <tr v-for="(value, key) in filterData(selectedRequest.requested_data)" :key="'desk-'+key">
                <td style="text-transform: capitalize; font-weight: 600; color: #546e7a; padding-left: 15px;">
                  {{ formatKey(key) }}
                </td>
                <td style="word-break: break-word;" class="grey-text text-darken-1">
                  {{ formatDisplayValue(key, getOriginalValue(key)) }}
                </td> 
                <td style="word-break: break-word;" :class="{'highlight-change': hasChanged(key, value)}">
                  <span v-if="hasChanged(key, value)" class="change-badge">CHANGED</span>
                  <span v-else class="unchanged-badge">No change</span>
                  <b :class="{'green-text text-darken-3': hasChanged(key, value)}">{{ formatDisplayValue(key, value) }}</b>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="hide-on-large-only" v-if="selectedRequest && selectedRequest.requested_data" style="margin-top: 15px;">
            <div 
              v-for="(value, key) in filterData(selectedRequest.requested_data)" 
              :key="'mob-comp-'+key" 
              class="card-panel z-depth-0" 
              style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 15px; margin-bottom: 12px; background-color: #fff;"
              :class="{'mobile-highlight-card': hasChanged(key, value)}"
            >
              <div style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f0f0f0; padding-bottom: 8px; margin-bottom: 10px;">
                <span style="font-weight: 700; color: #37474f; text-transform: capitalize;">{{ formatKey(key) }}</span>
                <span v-if="hasChanged(key, value)" class="change-badge" style="margin-right: 0;">CHANGED</span>
                <span v-else class="unchanged-badge" style="margin-right: 0;">Unchanged</span>
              </div>
              
              <div class="row" style="margin-bottom: 0;">
                <div class="col s6" style="padding-left: 0; border-right: 1px dashed #e0e0e0;">
                  <span style="font-size: 0.75rem; color: #9e9e9e; text-transform: uppercase; letter-spacing: 0.5px;">Current</span><br>
                  <span class="grey-text text-darken-1" style="word-break: break-word; font-size: 0.95rem;">
                    {{ formatDisplayValue(key, getOriginalValue(key)) }}
                  </span>
                </div>
                <div class="col s6" style="padding-right: 0; padding-left: 10px;">
                  <span style="font-size: 0.75rem; color: #1565c0; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 600;">Requested</span><br>
                  <b :class="{'green-text text-darken-3': hasChanged(key, value)}" style="word-break: break-word; font-size: 0.95rem;">
                    {{ formatDisplayValue(key, value) }}
                  </b>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div class="modal-footer grey lighten-3 custom-footer" style="justify-content: flex-end; padding-right: 24px;">
        <button 
          @click="promptReject(selectedRequest.id)" 
          class="btn red darken-1" 
          style="border-radius: 6px; margin-right: 10px;"
          :disabled="actionLoader === selectedRequest?.id"
        >
          Reject
        </button>
        <button 
          @click="promptApprove(selectedRequest.id)" 
          class="btn green darken-1" 
          style="border-radius: 6px;"
          :disabled="actionLoader === selectedRequest?.id"
        >
          <i class="material-icons left hide-on-small-only" style="margin-right: 5px;">check</i>
          Approve <span class="hide-on-small-only">& Save</span>
        </button>
      </div>
    </div>

    <!-- Confirm Approve Modal -->
    <div id="confirmApproveModal" class="modal">
      <div class="modal-content center-align">
        <i class="material-icons green-text" style="font-size: 4rem; margin-bottom: 10px;">check_circle</i>
        <h5 style="font-weight: 600; margin-top: 0;">Confirm Approval</h5>
        <p class="grey-text text-darken-2">Are you sure you want to approve and apply these changes to the employee's main record?</p>
      </div>
      <div class="modal-footer grey lighten-4" style="display: flex; justify-content: center; padding: 15px; height: auto;">
        <button @click="cancelApprove" type="button" class="btn-flat waves-grey" style="margin-right: 10px; border-radius: 6px;">Cancel</button>
        <button @click="executeApprove" class="btn green darken-1" style="border-radius: 6px;" :disabled="actionLoader === requestToApprove">
          <span v-if="actionLoader === requestToApprove">Approving...</span>
          <span v-else>Yes, Approve</span>
        </button>
      </div>
    </div>

    <!-- Confirm Reject Modal -->
    <div id="confirmRejectModal" class="modal">
      <div class="modal-content center-align">
        <i class="material-icons red-text" style="font-size: 4rem; margin-bottom: 10px;">warning</i>
        <h5 style="font-weight: 600; margin-top: 0;">Confirm Rejection</h5>
        <p class="grey-text text-darken-2">Are you sure you want to reject these edits? The staging data will be marked as rejected.</p>
      </div>
      <div class="modal-footer grey lighten-4" style="display: flex; justify-content: center; padding: 15px; height: auto;">
        <button @click="cancelReject" type="button" class="btn-flat waves-grey" style="margin-right: 10px; border-radius: 6px;">Cancel</button>
        <button @click="executeReject" class="btn red darken-1" style="border-radius: 6px;" :disabled="actionLoader === requestToReject">
          <span v-if="actionLoader === requestToReject">Rejecting...</span>
          <span v-else>Yes, Reject</span>
        </button>
      </div>
    </div>

  </AdminAppLayout>
</template>

<script>
import { api } from "@/axios";
import AdminAppLayout from "@/views/layout/AdminAppLayout.vue";

export default {
  name: "EditRequestsView",
  components: {
    AdminAppLayout,
  },
  data() {
    return {
      requests: [],
      loader: true,
      actionLoader: null, 
      selectedRequest: null,
      requestToApprove: null,
      requestToReject: null,
      requestedBy: null,
      dropdowns: {
        positions: [],
        plantillas: [],
        eligibilities: [],
        employmentStatuses: [],
        serviceStatuses: [],
        offices: [],
        salarySteps: []
      }
    };
  },
  methods: {
    // Fetch dropdowns so we can translate IDs to Names
    async getListForDropdowns() {
      await api.get('/dropdowns-lists')
        .then((res) => {
            this.dropdowns.positions = res.data.positions || [];
            this.dropdowns.plantillas = res.data.plantillas || [];
            this.dropdowns.eligibilities = res.data.eligibilities || [];
            this.dropdowns.employmentStatuses = res.data.employmentStatuses || [];
            this.dropdowns.serviceStatuses = res.data.serviceStatuses || [];
            this.dropdowns.offices = res.data.offices || [];
            this.dropdowns.salarySteps = res.data.salarySteps || [];
        })
        .catch(() => {
            console.error("An error occurred fetching dropdown lists for mappings.");
        });
    },

    async fetchRequests() {
      this.loader = true;
      await api.get('/edit-requests')
        .then(res => {
          this.requests = res.data;
          console.log('Fetched edit requests:', this.requests);

          const queryId = this.$route.query.id;
          if (queryId) {
            const targetRequest = this.requests.find(r => r.id == queryId);
            if (targetRequest) {
              this.openViewModal(targetRequest);
              window.history.replaceState({}, document.title, this.$route.path);
            }
          }
        })
        .catch(err => {
          console.error(err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to load edit requests.', classes: 'red'});
          }
        })
        .finally(() => {
          this.loader = false;
        });
    },

    openViewModal(request) {
      this.selectedRequest = request;

      api.get(`/employee/${this.selectedRequest.requested_by}`)
        .then(res => {
          console.log('Fetched employee details:', res.data);
          this.requestedBy = res.data.first_name + ' ' + res.data.last_name;
        })
        .catch(err => {
          console.error(err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to load employee details.', classes: 'red'});
          }
        });
      this.$nextTick(() => {
        const elem = document.getElementById('viewEditDiv');
        let instance = window.M.Modal.getInstance(elem);
        if (!instance) {
          instance = window.M.Modal.init(elem);
        }
        instance.open();
      });
    },

    promptApprove(id) {
      this.requestToApprove = id;
      this.$nextTick(() => {
        const elem = document.getElementById('confirmApproveModal');
        let instance = window.M.Modal.getInstance(elem);
        if (!instance) {
          instance = window.M.Modal.init(elem);
        }
        instance.open();
      });
    },

    promptReject(id) {
      this.requestToReject = id;
      this.$nextTick(() => {
        const elem = document.getElementById('confirmRejectModal');
        let instance = window.M.Modal.getInstance(elem);
        if (!instance) {
          instance = window.M.Modal.init(elem);
        }
        instance.open();
      });
    },

    cancelApprove() {
      const elem = document.getElementById('confirmApproveModal');
      const instance = window.M.Modal.getInstance(elem);
      if (instance) {
        instance.close();
      }
      this.requestToApprove = null;
    },

    cancelReject() {
      const elem = document.getElementById('confirmRejectModal');
      const instance = window.M.Modal.getInstance(elem);
      if (instance) {
        instance.close();
      }
      this.requestToReject = null;
    },

    async executeApprove() {
      const id = this.requestToApprove;
      if (!id) return;
      
      this.actionLoader = id;
      await api.post(`/edit-requests/${id}/approve`)
        .then(res => {
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: res.data.message, classes: 'green'});
          }
          
          // Close Confirm Modal
          const confirmElem = document.getElementById('confirmApproveModal');
          const confirmInstance = window.M.Modal.getInstance(confirmElem);
          if (confirmInstance) confirmInstance.close();

          // Close View Modal if it's open
          const viewElem = document.getElementById('viewEditDiv');
          const viewInstance = window.M.Modal.getInstance(viewElem);
          if (viewInstance) viewInstance.close();

          this.fetchRequests(); 
        })
        .catch(err => {
          console.error(err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to approve changes.', classes: 'red'});
          }
        })
        .finally(() => {
          this.actionLoader = null;
          this.requestToApprove = null;
        });
    },

    async executeReject() {
      const id = this.requestToReject;
      if (!id) return;

      this.actionLoader = id;
      await api.post(`/edit-requests/${id}/reject`)
        .then(res => {
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: res.data.message, classes: 'orange darken-2'});
          }

          // Close Confirm Modal
          const confirmElem = document.getElementById('confirmRejectModal');
          const confirmInstance = window.M.Modal.getInstance(confirmElem);
          if (confirmInstance) confirmInstance.close();

          // Close View Modal if it's open
          const viewElem = document.getElementById('viewEditDiv');
          const viewInstance = window.M.Modal.getInstance(viewElem);
          if (viewInstance) viewInstance.close();

          this.fetchRequests(); 
        })
        .catch(err => {
          console.error(err);
          if (typeof window.M !== 'undefined') {
            window.M.toast({html: 'Failed to reject request.', classes: 'red'});
          }
        })
        .finally(() => {
          this.actionLoader = null;
          this.requestToReject = null;
        });
    },

    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-PH', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    // format currency for items like base_salary
    formatCurrency(amount) {
      if (amount === null || amount === undefined || amount === '') {
        return '0.00';
      }
      return Number(amount).toLocaleString('en-PH', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // Filter out internal fields and explicitly exclude salary_grade_id
    filterData(data) {
      if (!data) return {};
      const hiddenFields = [
        'id', 'created_at', 'updated_at', 'deleted_at', 'user_id', 'employee_id',
        'salary_grade_id' // Added here per your requirement
      ];
      
      const filtered = {};
      for (let key in data) {
        if (!hiddenFields.includes(key)) {
          filtered[key] = data[key];
        }
      }
      return filtered;
    },

    // Ensure visual keys look pretty on the frontend table
    formatKey(key) {
      if (key === 'employee_id_number') return 'LTO ID Number';
      if (key === 'employment_status_id') return 'Employment Status';
      if (key === 'service_status_id') return 'Service Status';
      if (key === 'position_id') return 'Position';
      if (key === 'plantilla_id') return 'Plantilla';
      if (key === 'eligibility_id') return 'Eligibility';
      if (key === 'assigned_office_id') return 'Assigned Office';
      return key.replace(/_/g, ' ');
    },

    getOriginalValue(key) {
      if (!this.selectedRequest || !this.selectedRequest.employee) return null;
      const emp = this.selectedRequest.employee;
      
      // Nested original value catchers
      if (key === 'email' || key === 'username') return emp.user ? emp.user[key] : null;
      if (key === 'salary_grade') return emp.salary_grade ? emp.salary_grade.grade : null;
      if (key === 'salary_step') return emp.salary_grade ? emp.salary_grade.step : null;
      
      return emp[key];
    },

    // Look up the names matching the IDs using dropdown lists so user sees string instead of Int ID
    formatDisplayValue(key, value) {
      if (value === null || value === '' || value === undefined) return 'Empty / N/A';
      if (key === 'is_locked') return value == 1 ? 'Yes' : 'No';

      if (key === 'position_id') {
        const item = this.dropdowns.positions.find(i => i.id == value);
        return item ? item.title : value;
      }
      if (key === 'plantilla_id') {
        const item = this.dropdowns.plantillas.find(i => i.id == value);
        return item ? item.name : value;
      }
      if (key === 'eligibility_id') {
        const item = this.dropdowns.eligibilities.find(i => i.id == value);
        return item ? item.name : value;
      }
      if (key === 'employment_status_id') {
        const item = this.dropdowns.employmentStatuses.find(i => i.id == value);
        return item ? item.name : value;
      }
      if (key === 'service_status_id') {
        const item = this.dropdowns.serviceStatuses.find(i => i.id == value);
        return item ? item.name : value;
      }
      if (key === 'assigned_office_id') {
        const item = this.dropdowns.offices.find(i => i.id == value);
        return item ? item.name : value;
      }
      if (key === 'base_salary') {
        return `₱${this.formatCurrency(value)}`;
      }

      if (key.includes('date') || key === 'birthday') {
        try {
          const datePart = String(value).split('T')[0];
          const [year, month, day] = datePart.split('-');
          const dateObj = new Date(year, month - 1, day);
          if (!isNaN(dateObj.getTime())) {
            return dateObj.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            });
          }
        } catch (e) {
          // Fallback to original value if parsing fails
        }
      }

      return value;
    },

    hasChanged(key, newValue) {
      let originalValue = this.getOriginalValue(key);

      let origStr = originalValue === null || originalValue === undefined ? '' : String(originalValue).trim();
      let newStr = newValue === null || newValue === undefined ? '' : String(newValue).trim();

      if (key.includes('date') || key === 'birthday') {
        origStr = origStr.split('T')[0].split(' ')[0]; 
        newStr = newStr.split('T')[0].split(' ')[0];
      }

      return origStr !== newStr;
    }
  },
  mounted() {
    this.getListForDropdowns();
    this.fetchRequests();
    
    this.$nextTick(() => {
      if (typeof window.M !== 'undefined') {
        const modals = document.querySelectorAll('.modal');
        window.M.Modal.init(modals);
      }
    });
  }
};
</script>

<style scoped>
@media only screen and (max-width: 992px) {
  .main-modal { width: 95%; }
}
@media only screen and (min-width: 993px) {
  .main-modal { width: 80%; }
}

.main-modal {
  border-radius: 20px;
  height: 85% !important;
  max-height: 85% !important;
}

.main-modal.modal-fixed-footer .modal-content {
  height: calc(100% - 65px) !important; 
}
.main-modal:not(.modal-fixed-footer) .modal-content {
  height: 100% !important;
}

.modal-header {
  border-bottom: 1px solid rgba(0,0,0,0.1);
}

.close-x-btn {
  transition: background-color 0.2s ease;
}
.close-x-btn:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

.custom-footer {
  display: flex !important;
  align-items: center !important;
  overflow: hidden !important; 
  border-radius: 0 0 20px 20px;
  height: 65px !important; 
}

#confirmApproveModal {
  width: 420px !important;
  max-width: 90%;
  border-radius: 16px;
  min-height: auto;
  overflow: hidden;
  top: 30% !important; 
}

#confirmRejectModal {
  width: 420px !important;
  max-width: 90%;
  border-radius: 16px;
  min-height: auto;
  overflow: hidden;
  top: 30% !important; 
}

.table-responsive-wrapper {
  overflow-x: auto;
  width: 100%;
}
.nowrap {
  white-space: nowrap;
}

.highlight-change {
  background-color: #e8f5e9 !important; 
  border-left: 4px solid #4caf50 !important; 
  transition: background-color 0.3s ease;
}

.mobile-highlight-card {
  border-left: 4px solid #4caf50 !important;
  background-color: #fafdfa !important;
}

.change-badge {
  background-color: #4caf50;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 3px 6px;
  border-radius: 4px;
  margin-right: 8px;
  vertical-align: middle;
  letter-spacing: 0.5px;
}
.unchanged-badge {
  background-color: #905900;
  color: white;
  font-size: 0.7rem;
  font-weight: bold;
  padding: 3px 6px;
  border-radius: 4px;
  margin-right: 8px;
  vertical-align: middle;
  letter-spacing: 0.5px;
}

</style>